import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Download, Trash2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';

function TemplateDetails({ user }) {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [phases, setPhases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPhaseDialogOpen, setIsPhaseDialogOpen] = useState(false);
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
  const [newPhase, setNewPhase] = useState({ name: '', category: 'general', description: '', estimated_cost: 0, exclude_from_sqft: false });
  const [projectData, setProjectData] = useState({ name: '', address: '', units: 1 });

  useEffect(() => {
    loadTemplateData();
  }, [templateId]);

  const loadTemplateData = async () => {
    try {
      const [templateRes, phasesRes] = await Promise.all([
        apiService.getTemplate(templateId),
        apiService.getTemplatePhases(templateId),
      ]);
      setTemplate(templateRes.data);
      setPhases(phasesRes.data);
    } catch (error) {
      toast.error('Failed to load template');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePhase = async (e) => {
    e.preventDefault();
    try {
      await apiService.createTemplatePhase({
        ...newPhase,
        template_id: templateId,
        estimated_cost: parseFloat(newPhase.estimated_cost),
      });
      toast.success('Phase added to template!');
      setIsPhaseDialogOpen(false);
      setNewPhase({ name: '', category: 'general', description: '', estimated_cost: 0, exclude_from_sqft: false });
      loadTemplateData();
    } catch (error) {
      toast.error('Failed to add phase');
    }
  };

  const handleDeletePhase = async (phaseId) => {
    try {
      await apiService.deleteTemplatePhase(phaseId);
      toast.success('Phase removed');
      loadTemplateData();
    } catch (error) {
      toast.error('Failed to remove phase');
    }
  };

  const handleExport = async () => {
    try {
      const response = await apiService.exportTemplate(templateId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${template.name}_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template exported! Ready for bank submission.');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const response = await apiService.createProjectFromTemplate(templateId, {
        ...projectData,
        user_id: user.id,
        units: parseInt(projectData.units),
      });
      toast.success('Project created with all estimates loaded!');
      navigate(`/project/${response.data.project_id}`);
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  if (isLoading || !template) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-slate-600">Loading template...</p>
        </div>
      </div>
    );
  }

  const totalEstimated = phases.reduce((sum, p) => sum + p.estimated_cost, 0);
  const costPerSqft = totalEstimated / template.sqft;
  const ltv = template.sales_price > 0 ? (totalEstimated / template.sales_price * 100) : 0;

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/templates')} variant="ghost">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{template.name}</h1>
                <p className="text-sm text-slate-500">Budget Template</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={handleExport} variant="outline" className="rounded-full">
                <Download className="w-4 h-4 mr-2" />
                Export for Bank
              </Button>
              <Dialog open={isProjectDialogOpen} onOpenChange={setIsProjectDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full">
                    <Play className="w-4 h-4 mr-2" />
                    Start Project
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Project from Template</DialogTitle>
                    <DialogDescription>All estimates will be pre-loaded</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateProject} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Project Name</Label>
                      <Input
                        placeholder="123 Main St Build"
                        value={projectData.name}
                        onChange={(e) => setProjectData({ ...projectData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Address</Label>
                      <Input
                        placeholder="123 Main St, City, State"
                        value={projectData.address}
                        onChange={(e) => setProjectData({ ...projectData, address: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Number of Units</Label>
                      <Input
                        type="number"
                        min="1"
                        value={projectData.units}
                        onChange={(e) => setProjectData({ ...projectData, units: e.target.value })}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full">Create Project</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Template Summary */}
        <Card className="bg-gradient-to-br from-blue-600 to-blue-700 text-white mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">Template Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <p className="text-blue-100 text-sm mb-1">Square Feet</p>
                <p className="text-3xl font-bold">{template.sqft.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-blue-100 text-sm mb-1">Est. Budget</p>
                <p className="text-3xl font-bold">${totalEstimated.toLocaleString()}</p>
                <p className="text-xs text-blue-200 mt-1">${costPerSqft.toFixed(2)}/sqft</p>
              </div>
              <div>
                <p className="text-blue-100 text-sm mb-1">Total w/ Lot</p>
                <p className="text-3xl font-bold">${(totalEstimated + template.lot_cost).toLocaleString()}</p>
              </div>
              {template.sales_price > 0 && (
                <div>
                  <p className="text-blue-100 text-sm mb-1">Est. LTV</p>
                  <p className="text-3xl font-bold">{ltv.toFixed(1)}%</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Phases */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-900">Cost Breakdown</h2>
          <Dialog open={isPhaseDialogOpen} onOpenChange={setIsPhaseDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Line Item
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Cost Line Item</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreatePhase} className="space-y-4">
                <div className="space-y-2">
                  <Label>Item Name</Label>
                  <Input
                    placeholder="Foundation Work"
                    value={newPhase.name}
                    onChange={(e) => setNewPhase({ ...newPhase, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Input
                    placeholder="foundation"
                    value={newPhase.category}
                    onChange={(e) => setNewPhase({ ...newPhase, category: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Estimated Cost ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newPhase.estimated_cost}
                    onChange={(e) => setNewPhase({ ...newPhase, estimated_cost: e.target.value })}
                    required
                  />
                </div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={newPhase.exclude_from_sqft}
                    onChange={(e) => setNewPhase({ ...newPhase, exclude_from_sqft: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Exclude from $/sqft (permits, fees, etc.)</span>
                </label>
                <Button type="submit" className="w-full">Add to Template</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-4">
          {phases.map((phase) => (
            <Card key={phase.id} className="bg-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{phase.name}</h3>
                    <p className="text-sm text-slate-500 capitalize">{phase.category.replace('_', ' ')}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-lg font-bold text-slate-900">${phase.estimated_cost.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">
                        {phase.exclude_from_sqft ? 'N/A' : `$${(phase.estimated_cost / template.sqft).toFixed(2)}/sqft`}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleDeletePhase(phase.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {phases.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-slate-500 mb-4">No cost items yet</p>
                <Button onClick={() => setIsPhaseDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Item
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}

export default TemplateDetails;
